import React, { useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { 
  Paper, 
  Button, 
  IconButton, 
  TextField, 
  Box, 
  Typography, 
  CircularProgress, 
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getIcon } from '../../../../utils/IconHandler';
import SitesService from '../../../../services/SitesService';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

const CategoriesTab = ({ categories, onCategoriesChange }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [formData, setFormData] = useState({ name: '', orderPosition: 0 });

  const handleCreate = () => {
    setFormData({ name: '', orderPosition: 0 });
    setIsCreateModalOpen(true);
  };

  const handleEdit = (category) => {
    setSelectedCategory(category);
    setFormData({ name: category.name, orderPosition: category.orderPosition });
    setIsEditModalOpen(true);
  };

  const handleDelete = (category) => {
    setSelectedCategory(category);
    setIsDeleteDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const payload = { ...formData, orderPosition: parseInt(formData.orderPosition, 10) || 0 };

      if (isCreateModalOpen) {
        await SitesService.createSiteProductCategory(payload);
      } else {
        await SitesService.updateSiteProductCategory(selectedCategory.id, payload);
      }
      
      setIsCreateModalOpen(false);
      setIsEditModalOpen(false);
      setSelectedCategory(null);
      onCategoriesChange();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al guardar categoría');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      setError(null);
      await SitesService.deleteSiteProductCategory(selectedCategory.id);
      setIsDeleteDialogOpen(false);
      setSelectedCategory(null);
      onCategoriesChange();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al eliminar categoría');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { 
      field: 'id', 
      headerName: 'ID', 
      width: 80 
    },
    { 
      field: 'name', 
      headerName: t('name') || 'Nombre', 
      flex: 1 
    },
    { 
      field: 'orderPosition', 
      headerName: t('order') || 'Orden', 
      width: 120 
    },
    {
      field: 'actions',
      headerName: t('actions') || 'Acciones',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton 
            size="small" 
            onClick={() => handleEdit(params.row)}
            color="primary"
          >
            <EditIcon />
          </IconButton>
          <IconButton 
            size="small" 
            onClick={() => handleDelete(params.row)}
            color="error"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      )
    }
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">{t('categories') || 'Categorías'}</Typography>
        <Button 
          variant="contained" 
          startIcon={getIcon('add')} 
          onClick={handleCreate}
          disabled={loading}
        >
          {t('create_category') || 'Crear Categoría'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <DataGrid
        rows={categories}
        columns={columns}
        getRowId={(row) => row.id}
        autoHeight
        loading={loading}
        pageSizeOptions={[10, 25, 50]}
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } }
        }}
      />

      {/* Modal Crear/Editar */}
      <Dialog 
        open={isCreateModalOpen || isEditModalOpen} 
        onClose={() => {
          setIsCreateModalOpen(false);
          setIsEditModalOpen(false);
          setSelectedCategory(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {isCreateModalOpen ? (t('create_category') || 'Crear Categoría') : (t('edit_category') || 'Editar Categoría')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label={t('name') || 'Nombre'}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label={t('order') || 'Orden'}
              type="number"
              value={formData.orderPosition}
              onChange={(e) => {
                const value = e.target.value;
                setFormData({ ...formData, orderPosition: value === '' ? '' : parseInt(value, 10) });
              }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setIsCreateModalOpen(false);
            setIsEditModalOpen(false);
            setSelectedCategory(null);
          }}>
            {t('cancel') || 'Cancelar'}
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={loading || !formData.name}>
            {t('save') || 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Eliminar */}
      <Dialog open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)}>
        <DialogTitle>{t('confirm_delete') || 'Confirmar Eliminación'}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('confirm_delete_category', { name: selectedCategory?.name }) || `¿Estás seguro de que deseas eliminar la categoría "${selectedCategory?.name}"?`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)}>
            {t('cancel') || 'Cancelar'}
          </Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error" disabled={loading}>
            {t('delete') || 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CategoriesTab;
